import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import AdminUserAPI from "../api/AdminUserAPI";
import FastAPI from "../api/FastAPI";

import type { AdminRoleScope } from "../components/adminUsers/adminRoleScopes.types";
import type { AppState } from "../reducers";
import type { AdminRoleScopeOptions } from "./useAdminRoleScopeOptions.types";

// `isLoading` ne vaut vrai que pendant la lecture : une requête désactivée reste « loading ».
function useAdminRoleScopeOptions(
  scope: AdminRoleScope,
  excludedIds: string[],
  enabled: boolean,
): AdminRoleScopeOptions {
  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config.data,
  );

  const projectsQuery = useQuery({
    ...AdminUserAPI.generateQuery({
      fn: AdminUserAPI.getAdminProjects,
      params: [],
    }),
    enabled: enabled && scope === "project",
  });

  const channelsQuery = useQuery({
    ...FastAPI.generateQuery({ fn: FastAPI.getChannelsList, params: [] }),
    enabled: enabled && scope === "channel",
  });

  if (scope === "organization") {
    return {
      options: [
        {
          value: orgaConfig.organization_uuid,
          label: orgaConfig.organization_label,
        },
      ],
      isLoading: false,
      isError: false,
    };
  }

  if (scope === "project") {
    return {
      options: (projectsQuery.data ?? [])
        .filter(
          (project) => !excludedIds.includes(project.storefront_project_uuid),
        )
        .map((project) => ({
          value: project.storefront_project_uuid,
          label: project.storefront_project_name,
        })),
      isLoading: projectsQuery.isInitialLoading,
      isError: projectsQuery.isError,
    };
  }

  return {
    options: (channelsQuery.data?.data ?? [])
      .filter((channel) => !excludedIds.includes(channel.uuid))
      .map((channel) => ({ value: channel.uuid, label: channel.name })),
    isLoading: channelsQuery.isInitialLoading,
    isError: channelsQuery.isError,
  };
}

export default useAdminRoleScopeOptions;
